import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowRight, ShieldCheck, Clock, Wrench, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { IMG } from "@/data/site";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { estimateCost } from "@/lib/api";
import { toast } from "sonner";
import { useLead } from "@/context/LeadContext";

const PROPERTY = ["Residential", "Commercial", "Industrial"];
const AREAS = ["Roof", "Terrace", "Bathroom", "Basement", "Water Tank", "Walls", "Exterior"];
const SEVERITY = ["Minor", "Moderate", "Severe"];

export default function CostEstimator() {
  const [form, setForm] = useState({ property_type: "Residential", service_area: "Roof", severity: "Moderate", approx_area_sqft: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { openLead } = useLead();

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const calculate = async () => {
    const area = parseFloat(form.approx_area_sqft);
    if (!area || area <= 0) return toast.error("Please enter a valid area in sq.ft");
    setLoading(true);
    try {
      const r = await estimateCost({ ...form, approx_area_sqft: area });
      setResult(r);
    } catch { toast.error("Could not calculate. Please try again."); } finally { setLoading(false); }
  };

  const fmt = (n) => "₹" + n.toLocaleString("en-IN");

  return (
    <div data-testid="cost-estimator-page">
      <PageHero
        title="Instant Cost Estimator"
        subtitle="Get a transparent ballpark for your waterproofing project in seconds. Final pricing is confirmed after a free on-site inspection."
        image={IMG.epoxy}
        crumbs={[{ label: "Cost Estimator" }]}
      />

      <section className="bg-soft-grey py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-8 items-start">
          <div className="bg-white rounded-lg p-8 border border-slate-200">
            <div className="flex items-center gap-2 mb-6"><Calculator className="h-6 w-6 text-royal" /><h2 className="font-display text-2xl font-extrabold text-navy-deep">Your Details</h2></div>
            <div className="space-y-5">
              <Field label="Property Type">
                <Select value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger data-testid="est-property"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROPERTY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Leakage Area">
                <Select value={form.service_area} onValueChange={set("service_area")}>
                  <SelectTrigger data-testid="est-area"><SelectValue /></SelectTrigger>
                  <SelectContent>{AREAS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Problem Severity">
                <Select value={form.severity} onValueChange={set("severity")}>
                  <SelectTrigger data-testid="est-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Approximate Area (sq.ft)">
                <Input data-testid="est-sqft" type="number" value={form.approx_area_sqft} onChange={set("approx_area_sqft")} placeholder="e.g. 800" />
              </Field>
              <button onClick={calculate} disabled={loading} className="btn-ripple w-full bg-royal hover:bg-royal/90 text-white font-semibold py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="est-calculate">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                {loading ? "Calculating..." : "Calculate Estimate"}
              </button>
            </div>
          </div>

          <div className="lg:sticky lg:top-28">
            {result ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-navy-deep rounded-lg p-8 text-white relative overflow-hidden" data-testid="est-result">
                <div className="absolute -top-16 -right-16 h-48 w-48 bg-royal/30 blur-3xl rounded-full" />
                <div className="text-xs uppercase tracking-widest text-sky relative">Estimated Cost Range</div>
                <div className="font-display text-4xl md:text-5xl font-extrabold text-gradient-blue mt-2 relative">
                  {fmt(result.low)} – {fmt(result.high)}
                </div>
                <div className="mt-6 space-y-3 relative">
                  <Row icon={Wrench} label="Recommended System" value={result.recommended_system} />
                  <Row icon={ShieldCheck} label="Warranty" value={result.warranty} />
                  <Row icon={Clock} label="Estimated Timeline" value={result.timeline} />
                </div>
                <p className="text-slate-400 text-xs mt-5 relative">*Indicative range. Exact quote confirmed after free inspection.</p>
                <button onClick={() => openLead("book_inspection", { prefill: { service: `${form.service_area} Waterproofing`, property_type: form.property_type, message: `Estimator: ${result.low}-${result.high} for ${form.approx_area_sqft} sq.ft` } })} className="btn-ripple w-full bg-royal hover:bg-royal/90 text-white font-semibold py-3.5 rounded-sm mt-6 transition-colors flex items-center justify-center gap-2 relative" data-testid="est-book">
                  Book Inspection to Confirm <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            ) : (
              <div className="bg-white rounded-lg p-10 border border-dashed border-slate-300 text-center">
                <Calculator className="h-12 w-12 text-slate-300 mx-auto" />
                <p className="text-slate-500 mt-4">Fill in your details and hit calculate to see your instant estimate.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-navy-deep font-medium">{label}</Label>{children}</div>
);
const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3"><Icon className="h-5 w-5 text-sky mt-0.5 shrink-0" /><div><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-slate-100 text-sm">{value}</div></div></div>
);
